import React from 'react';
import { connect } from 'react-redux';

import {
  workflowSetDefaultTicketName,
  workflowSetTicketNameConventions,
} from '~/actions/workflow';
import {
  TICKET_NAMING_CONVENTION_ALLOWED_IMPLICIT_TYPES,
  TICKET_NAMING_CONVENTION_ALLOWED_TYPES,
} from '~/common/constants/ticket';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { useIntl } from 'react-intl';
import Checkbox from '~/ui/components/Checkbox/Checkbox';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { withInputError } from '~/ui/hocs/form';
import { testValidFieldsInTicketNamingConvention } from '~/utils/ticket';
import { pathObjectToString } from '~/utils/workflow';

import Section from '../Section';
import TicketNameInput from './TicketNameInput';

const SYSTEM_DEFAULT_NAME = 'Workflow Name_Counterparties_Ticket Created Date';

function TicketNamePreview({
  useDefaultName,
  ticketNamingConvention,
  fields,
  id,
  error,
}) {
  const previewedName = useDefaultName
    ? SYSTEM_DEFAULT_NAME
    : pathObjectToString(ticketNamingConvention, fields);
  return (
    <FlexLayout flexDirection="column" space={2}>
      <FlexLayout
        id={id}
        alignItems="center"
        px={4}
        py={2}
        space={2}
        sx={{
          bg: 'black-alpha-04',
          border: error ? 'border' : 'none',
          borderColor: error ? 'red-400' : 'none',
          borderRadius: 'm',
        }}
      >
        <Icon icon="review" color="status.info" />
        <Text color="gray-600" variant="xs-dense-medium">
          {previewedName}
        </Text>
      </FlexLayout>
    </FlexLayout>
  );
}

function validateTicketNamePreview({
  ticketNamingConvention,
  fields,
  useDefaultName,
  intl,
}) {
  const ticketNameString = pathObjectToString(ticketNamingConvention, fields);
  return ticketNameString || useDefaultName
    ? null
    : intl.formatMessage({
        id: 'TicketNamingConventions.invalidTicketNameError',
        defaultMessage: 'Invalid ticket name.',
        description: 'Error message for invalid ticket name preview',
      });
}

function formatNamingOutput(string) {
  return string.replace(/\//g, '');
}

function TicketName({
  //connected
  ticketNamingConvention,
  keepSystemDefaultTicketName,
  fields,
  workflowSetDefaultTicketName,
  workflowSetTicketNameConventions,
}) {
  const intl = useIntl();

  const hasErrors = !testValidFieldsInTicketNamingConvention(
    ticketNamingConvention,
    fields,
  );

  const TicketNamePreviewWithError = withInputError(
    TicketNamePreview,
    (props) => validateTicketNamePreview({ ...props, intl }),
  );

  const filteredFields = Object.entries(fields)
    .filter(
      ([key, value]) =>
        (TICKET_NAMING_CONVENTION_ALLOWED_TYPES.includes(value.type) &&
          key !== 'Date Signed' &&
          key !== 'Date Completed') ||
        TICKET_NAMING_CONVENTION_ALLOWED_IMPLICIT_TYPES.includes(key),
    )
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  return (
    <Section
      title={intl.formatMessage({
        id: 'TicketNamingConventions.sectionTitle',
        defaultMessage: 'Ticket Naming Conventions',
        description: 'Title for ticket naming conventions section',
      })}
      icon="review"
    >
      <TicketNameInput
        disabled={keepSystemDefaultTicketName}
        ticketNamingConvention={ticketNamingConvention}
        fields={filteredFields}
        id="workflow--settings-ticket-naming-format-entry"
        label={intl.formatMessage({
          id: 'TicketNamingConventions.ticketNamingFormatLabel',
          defaultMessage: 'Ticket naming format',
          description: 'Label for ticket naming format input',
        })}
        placeholder={intl.formatMessage({
          id: 'TicketNamingConventions.specifyTicketNamePlaceholder',
          defaultMessage: 'Specify ticket name',
          description: 'Placeholder text for ticket name input',
        })}
        formatOutput={formatNamingOutput}
        onChange={(ticketNamingConvention) =>
          workflowSetTicketNameConventions(ticketNamingConvention)
        }
        errorMessage={
          hasErrors
            ? intl.formatMessage({
                id: 'TicketNamingConventions.invalidFieldsError',
                defaultMessage: 'Invalid fields in ticket name.',
                description:
                  'Error message when ticket name contains invalid fields',
              })
            : ''
        }
      />
      <TicketNamePreviewWithError
        useDefaultName={keepSystemDefaultTicketName}
        ticketNamingConvention={ticketNamingConvention}
        fields={fields}
        id="workflow--settings-ticket-naming-convention-preview"
      />
      <Layout flexDirection="column" spacing={1}>
        <Checkbox
          label={intl.formatMessage({
            id: 'TicketNamingConventions.keepSystemDefaultLabel',
            defaultMessage: 'Keep system default name',
            description:
              'Label for checkbox to keep system default ticket name',
          })}
          value={keepSystemDefaultTicketName}
          onChange={workflowSetDefaultTicketName}
        />
        <Tooltip tooltip={`"${SYSTEM_DEFAULT_NAME}"`}>
          <Icon icon="info" color="gray-600" size="xs" />
        </Tooltip>
      </Layout>
    </Section>
  );
}

function mapStateToProps({ workflow }) {
  // only include fields that are assigned and have valid types defined.
  return {
    ticketNamingConvention: workflow.settings.ticketNamingConvention,
    keepSystemDefaultTicketName: workflow.settings.keepSystemDefaultTicketName,
    fields: workflow.fields,
  };
}

export default connect(mapStateToProps, {
  workflowSetDefaultTicketName,
  workflowSetTicketNameConventions,
})(TicketName);
